// Pure helpers used by match-card rendering and team labeling.
//
// `matchPlayerName` flattens the (sometimes-nested) participant object and
// appends `(게)` for guest participants — that visual marker is consistent
// across the schedule, ranking, and result-share views.
//
// `resizeImageToDataUrl` is here because club-logo and result-share previews
// both need it; keeping it in matches/ matches its primary caller (match-card
// avatar uploads) and avoids creating a one-off `ui/image.ts` module.

export function matchPlayerName(p: any): string {
  if (!p) return "-";
  const inner = p.participant || p;
  const raw = inner.display_name || p.display_name || "-";
  if (raw === "-") return raw;
  const type = inner.participant_type || p.participant_type;
  return type === "guest" ? `${raw}(게)` : raw;
}

export function formatTeamName(name: string | null | undefined): string {
  const t = String(name || "").trim();
  if (!t) return "";
  if (t.startsWith("팀 ") || t.startsWith("팀\u00a0")) return t;
  return `팀 ${t}`;
}

export interface ResizedImage {
  dataUrl: string;
  type: string;
}

export function resizeImageToDataUrl(file: File, maxDim: number): Promise<ResizedImage> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("파일을 읽지 못했어요"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("이미지 디코딩에 실패했어요"));
      img.onload = () => {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * scale));
        const hpx = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = hpx;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("캔버스 컨텍스트를 만들 수 없어요"));
          return;
        }
        ctx.drawImage(img, 0, 0, w, hpx);
        const type =
          file.type === "image/png"
            ? "image/png"
            : file.type === "image/webp"
              ? "image/webp"
              : "image/jpeg";
        const quality = type === "image/jpeg" ? 0.86 : undefined;
        resolve({ dataUrl: canvas.toDataURL(type, quality), type });
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  });
}
